import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { 
  Trophy, Target, Calendar, Users, Award, TrendingUp, 
  CheckCircle, Brain, Heart, Dumbbell, Apple, Moon,
  ArrowLeft, Home
} from 'lucide-react';

const QuickStartGuide = () => {
  const navigate = useNavigate();

  const sections = [
    {
      title: "Getting Started",
      icon: <Trophy className="w-8 h-8 text-yellow-500" />,
      steps: [
        {
          title: "Complete Your Profile",
          description: "Add your position, experience level, and athletic goals to personalize your experience.",
          icon: <CheckCircle className="w-5 h-5 text-emerald-500" />
        },
        {
          title: "Join Your Team",
          description: "Connect with your coach by requesting to join their team or accepting an invitation.",
          icon: <Users className="w-5 h-5 text-blue-500" />
        },
        {
          title: "Set Your Goals",
          description: "Define what you want to achieve - whether it's improving skills, fitness, or mental game.",
          icon: <Target className="w-5 h-5 text-red-500" />
        }
      ]
    },
    {
      title: "Daily Activities",
      icon: <Calendar className="w-8 h-8 text-blue-500" />,
      steps: [
        {
          title: "Daily Challenges",
          description: "Complete daily challenges in affirmations, visualization, and mental training to earn points and build streaks.",
          icon: <Brain className="w-5 h-5 text-purple-500" />
        },
        {
          title: "Track Your Wellness",
          description: "Log your nutrition, hydration, and sleep to maintain peak performance.",
          icon: <Heart className="w-5 h-5 text-red-500" />
        },
        {
          title: "Physical Activity",
          description: "Record your workouts and training sessions to track your physical progress.",
          icon: <Dumbbell className="w-5 h-5 text-orange-500" />
        }
      ]
    },
    {
      title: "Progress & Rewards",
      icon: <TrendingUp className="w-8 h-8 text-emerald-500" />,
      steps: [
        {
          title: "Earn Points",
          description: "Every completed challenge earns you points. Different difficulty levels reward more points.",
          icon: <Award className="w-5 h-5 text-yellow-500" />
        },
        {
          title: "Level Up",
          description: "As you earn points, you'll level up and unlock new achievements.",
          icon: <TrendingUp className="w-5 h-5 text-emerald-500" />
        },
        {
          title: "Build Streaks",
          description: "Complete activities consistently to build streaks and develop lasting habits.",
          icon: <Trophy className="w-5 h-5 text-yellow-500" />
        }
      ]
    },
    {
      title: "Wellness Modules",
      icon: <Heart className="w-8 h-8 text-red-500" />,
      steps: [
        {
          title: "Affirmations",
          description: "Start your day with positive affirmations to build mental strength and confidence.",
          icon: <Brain className="w-5 h-5 text-purple-500" />
        },
        {
          title: "Visualization",
          description: "Practice mental imagery to improve performance and achieve your goals.",
          icon: <Target className="w-5 h-5 text-blue-500" />
        },
        {
          title: "Nutrition Tracking",
          description: "Log your meals and get nutritional insights to fuel your performance.",
          icon: <Apple className="w-5 h-5 text-green-500" />
        },
        {
          title: "Hydration & Sleep",
          description: "Track your water intake and sleep patterns for optimal recovery.",
          icon: <Moon className="w-5 h-5 text-indigo-500" />
        }
      ]
    }
  ];

  const tips = [
    "Log in daily to maintain your streak and maximize your progress",
    "Complete all available challenges to earn maximum points",
    "Engage with your team and respond to coach challenges",
    "Track your wellness metrics consistently for better insights",
    "Set realistic goals and celebrate small wins along the way"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-950">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              className="text-yellow-400 hover:text-yellow-300 hover:bg-blue-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-yellow-500 hover:bg-yellow-600 text-blue-900"
            >
              <Home className="w-4 h-4 mr-2" />
              Go to Dashboard
            </Button>
          </div>
          
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-yellow-500 rounded-full mb-4">
              <Trophy className="w-10 h-10 text-blue-900" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              Athlete Quick Start Guide
            </h1>
            <p className="text-lg text-yellow-400">
              Everything you need to know to get started with Grind.365
            </p>
          </div>
        </div>

        {/* Main Sections */}
        <div className="space-y-6 mb-8">
          {sections.map((section) => (
            <Card key={section.title} className="bg-blue-900/95 backdrop-blur-sm border-blue-700">
              <CardHeader>
                <div className="flex items-center space-x-3">
                  {section.icon}
                  <CardTitle className="text-white text-2xl">{section.title}</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {section.steps.map((step) => (
                  <div key={step.title} className="flex items-start space-x-4 p-4 bg-blue-800/50 rounded-lg border border-blue-700">
                    <div className="flex-shrink-0 mt-1">
                      {step.icon}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-white mb-1">{step.title}</h4>
                      <p className="text-sm text-gray-300">{step.description}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Pro Tips */}
        <Card className="bg-gradient-to-br from-yellow-500 via-yellow-600 to-yellow-500 border-yellow-400 mb-8">
          <CardHeader>
            <CardTitle className="text-blue-900 text-2xl flex items-center">
              <Trophy className="w-6 h-6 mr-2" />
              Pro Tips for Success
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {tips.map((tip) => (
                <li key={tip.slice(0, 30)} className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-blue-900 flex-shrink-0 mt-0.5" />
                  <span className="text-blue-900 font-medium">{tip}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* FAQ Section */}
        <Card className="bg-blue-900/95 backdrop-blur-sm border-blue-700 mb-8">
          <CardHeader>
            <CardTitle className="text-white text-2xl">Frequently Asked Questions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <h4 className="font-semibold text-yellow-400">How do I join a team?</h4>
              <p className="text-gray-300 text-sm">
                Go to the Teams tab, search for your team by club name or team name, and submit a join request. 
                Your coach will review and approve your request.
              </p>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-semibold text-yellow-400">What are points and levels?</h4>
              <p className="text-gray-300 text-sm">
                You earn points by completing daily challenges and activities. As you accumulate points, 
                you level up, unlocking achievements and showing your commitment to improvement.
              </p>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-semibold text-yellow-400">How do streaks work?</h4>
              <p className="text-gray-300 text-sm">
                Streaks track consecutive days of activity. Complete at least one challenge or activity 
                each day to maintain your streak. Longer streaks demonstrate consistency and dedication.
              </p>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-semibold text-yellow-400">Can I customize my modules?</h4>
              <p className="text-gray-300 text-sm">
                Your coach controls which modules are available for your team. If you'd like to see different 
                modules, talk to your coach about enabling them.
              </p>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-semibold text-yellow-400">What if I miss a day?</h4>
              <p className="text-gray-300 text-sm">
                Don't worry! While your streak may reset, your total points and level are permanent. 
                Just jump back in the next day and keep building your progress.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Call to Action */}
        <div className="text-center">
          <Card className="bg-gradient-to-r from-emerald-500 to-blue-500 border-0">
            <CardContent className="py-8">
              <h2 className="text-3xl font-bold text-white mb-4">
                Ready to Start Your Journey?
              </h2>
              <p className="text-white text-lg mb-6">
                Head to your dashboard and complete your first challenge today!
              </p>
              <Button
                onClick={() => navigate('/dashboard')}
                size="lg"
                className="bg-yellow-500 hover:bg-yellow-600 text-blue-900 font-bold text-lg px-8"
              >
                <Home className="w-5 h-5 mr-2" />
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-gray-400 text-sm">
          <p>Need additional help? Contact your coach or team administrator.</p>
        </div>
      </div>
    </div>
  );
};

export default QuickStartGuide;
